import React from 'react';
import { Icon } from '@iconify/react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import style from '../scss/metricCard.module.scss';

/**
 * Reusable metric / stat card.
 *
 * @param {string} icon            Iconify icon name shown in the top-right badge.
 * @param {string} title           Card title.
 * @param {string|number} value    Main metric value.
 * @param {string|number} delta    Comparison amount (e.g. "20").
 * @param {'up'|'down'|'same'} trend  Controls the trend arrow icon + color (default 'up').
 * @param {string} comparisonText  Text after the delta (default "vs. last month").
 * @param {string} iconColor       Optional override for the badge icon color.
 * @param {string} iconBg          Optional override for the badge background color.
 * @param {string} className       Extra class for the outer card.
 * @param {boolean} loading        Shows a skeleton placeholder when true.
 */
const TREND_ICONS = {
    up: 'mdi:trending-up',
    down: 'mdi:trending-down',
    same: 'mdi:trending-neutral',
};

const MetricCardComponent = ({
    icon,
    title,
    value,
    delta,
    trend = 'up',
    comparisonText = 'vs. last month',
    iconColor,
    iconBg,
    className = '',
    loading = false,
}) => {
    const trendClass = trend === 'down' ? style.trendDown : trend === 'same' ? style.trendSame : style.trendUp;

    if (loading) {
        return (
            <div className={`${style.card} ${className}`.trim()}>
                <div className={style.top}>
                    <Skeleton width={120} height={14} />
                    <Skeleton circle width={36} height={36} />
                </div>
                <Skeleton width={70} height={30} />
                <div className={style.footer}>
                    <Skeleton width={110} height={14} />
                </div>
            </div>
        );
    }

    return (
        <div className={`${style.card} ${className}`.trim()}>
            <div className={style.top}>
                <p className={style.title}>{title}</p>
                {icon && (
                    <span
                        className={style.iconBadge}
                        style={{ backgroundColor: iconBg, color: iconColor }}
                    >
                        <Icon icon={icon} />
                    </span>
                )}
            </div>

            <p className={style.value}>{value}</p>

            {(delta !== undefined && delta !== null) && (
                <div className={style.footer}>
                    <Icon className={`${style.trendIcon} ${trendClass}`} icon={TREND_ICONS[trend] || TREND_ICONS.up} />
                    <span className={`${style.delta} ${trendClass}`}>{delta}</span>
                    <span className={style.comparison}>{comparisonText}</span>
                </div>
            )}
        </div>
    );
};

export default MetricCardComponent;
